import clsx from 'clsx';
import { useState, useRef, useLayoutEffect, useMemo } from 'react';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label?: string;
}

interface SegmentedControlProps<T extends string> {
  text?: string;
  value?: T;
  options: readonly T[] | SegmentedControlOption<T>[];
  onChange?: (value: T) => void;
  className?: string;
  'data-qa-selector'?: string;
}

export const SegmentedControl = <T extends string>({
  value,
  options,
  onChange,
  text,
  className,
  'data-qa-selector': dataQaSelector,
}: SegmentedControlProps<T>) => {
  const normalizedOptions = useMemo(() => {
    return options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt));
  }, [options]);

  const defaultValue = normalizedOptions[0]?.value;
  const selectedValue = value ?? defaultValue;

  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);

  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedValue];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [selectedValue]);

  const handleClick = (newValue: T) => {
    setIsAnimated(true);
    onChange?.(newValue);
  };

  const getDisplayLabel = (val: T) => {
    const opt = normalizedOptions.find((o) => o.value === val);
    return opt?.label ?? val;
  };

  return (
    <div className={clsx('flex w-full items-center justify-between', className)}>
      {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

      <div className="relative flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
        <div
          className={`absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0] ${isAnimated ? 'transition-all duration-300' : ''}`}
          style={{
            width: `${highlightStyle.width}px`,
            transform: `translateX(${highlightStyle.left}px)`,
          }}
        >
          <p className="text-button-sm !font-semibold capitalize">
            {getDisplayLabel(selectedValue)}
          </p>
        </div>

        {normalizedOptions.map((opt) => (
          <button
            key={opt.value}
            ref={(el) => {
              optionRefs.current[opt.value] = el;
            }}
            onClick={() => handleClick(opt.value)}
            className="text-button-sm relative z-10 w-16 cursor-pointer py-1.5 text-center text-neutral-400 capitalize"
            data-qa-selector={dataQaSelector ? `${dataQaSelector}-${opt.value}` : undefined}
          >
            {opt.label ?? opt.value}
          </button>
        ))}
      </div>
    </div>
  );
};
